import dayjs from "dayjs";
import { currentBankDurationOptionsMap, currentCompanyDurationOptionsMap } from "./const";
import { getClickId } from "./callApi";

/**
 * Build URL params for submit lead from contact redux data
 * @param {Object} contact - Contact data from Redux
 * @param {string} ssn - Full SSN number
 * @returns {string} - URL encoded query string
 */
export const buildSubmitLeadParams = (contact, ssn) => {
  // Format DOB to MM/DD/YYYY
  const formatDob = (dob) => {
    if (!dob) return "";
    try {
      return dayjs(dob).format("MM/DD/YYYY");
    } catch {
      return dob;
    }
  };

  // Format date to MM/DD/YYYY
  const formatDate = (date) => {
    if (!date) return "";
    try {
      return dayjs(date).format("MM/DD/YYYY");
    } catch {
      return date;
    }
  };

  // Get last 4 digits of SSN
  const getLast4SSN = (fullSSN) => {
    if (!fullSSN) return "";
    const cleaned = fullSSN.replace(/\D/g, "");
    return cleaned.slice(-4);
  };

  // Map own_home value (YES/NO to 1/0)
  const mapOwnHome = (value) => {
    if (!value) return "0";
    const val = String(value).toUpperCase();
    return val === "YES" || val === "OWN" || val === "1" || val === "TRUE" ? "1" : "0";
  };

  // Map direct_deposit value (YES/NO to 1/0)
  const mapDirectDeposit = (value) => {
    if (!value) return "0";
    const val = String(value).toUpperCase();
    return val === "YES" || val === "1" || val === "TRUE" ? "1" : "0";
  };

  // Map bank account type to lowercase
  const mapBankAccountType = (value) => {
    if (!value) return "checking";
    return value.toLowerCase();
  };

  // Map credit score to lowercase
  const mapCreditScore = (value) => {
    if (!value) return "fair";
    const scoreMap = {
      "EXCELLENT": "excellent",
      "GOOD": "good",
      "FAIR": "fair",
      "POOR": "poor",
      "VERY_POOR": "poor"
    };
    return scoreMap[String(value).toUpperCase()] || String(value).toLowerCase();
  };

  // Convert residence duration to months
  const mapResidenceDuration = (value) => {
    if (!value) return "12";
    // If it's already a number, return it
    if (!isNaN(value)) return String(value);
    // Map text values to months
    const durationMap = {
      "Less than 1 year": 6,
      "1 - 2 year": 18,
      "2 - 3 years": 30,
      "3 - 5 years": 48,
      "5+ years": 72,
      "More than 5 years": 72
    };
    return String(durationMap[value] || 12);
  };

  // Convert employment duration to months
  const mapEmploymentDuration = (value) => {
    if (!value) return "12";
    // If it's already a number, return it
    if (!isNaN(value)) return String(value);
    // Use const map or fallback
    return String(currentCompanyDurationOptionsMap[value] || 12);
  };

  // Convert bank duration to months
  const mapBankDuration = (value) => {
    if (!value) return "6";
    // If it's already a number, return it
    if (!isNaN(value)) return String(value);
    // Use const map or fallback
    return String(currentBankDurationOptionsMap[value] || 6);
  };

  // Build params object matching the expected format
  const params = {
    email: contact?.email || "",
    bday: formatDob(contact?.dob),
    last4ssn: getLast4SSN(ssn),
    phone: (contact?.phone || contact?.cellPhone || "").replace(/\D/g, ""),
    loan_purpose: contact?.loanReason || "",
    first_name: contact?.firstName || "",
    last_name: contact?.lastName || "",
    zip: contact?.zipCode || contact?.zip || "",
    address: contact?.streetAddress || contact?.address || "",
    state: contact?.state || "",
    city: contact?.city || "",
    address_length_months: mapResidenceDuration(contact?.addressLengthMonths || contact?.currentResidenceDuration),
    own_home: mapOwnHome(contact?.residenceOwnership),
    income_type: contact?.incomeType || contact?.incomeSource || "EMPLOYMENT",
    employed_months: mapEmploymentDuration(contact?.employedMonths || contact?.currentCompanyDuration),
    monthly_income: String(contact?.incomeNetMonthly || contact?.monthlyIncome || "").replace(/\D/g, ""),
    // pay_date1: formatDate(contact?.incomeNextDate1),
    employer: contact?.workCompanyName || contact?.employerName || "",
    job_title: contact?.jobTitle || "",
    work_phone: (contact?.workPhone || contact?.employerPhone || "").replace(/\D/g, ""),
    pay_frequency: contact?.incomePaymentFrequency || "BIWEEKLY",
    direct_deposit: mapDirectDeposit(contact?.bankDirectDeposit || contact?.getDirectDeposit),
    bank_aba: contact?.bankAba || contact?.routingNumber || "",
    bank_name: contact?.bankName || "",
    bank_account_type: mapBankAccountType(contact?.bankAccountType),
    bank_account_length_months: mapBankDuration(contact?.bankAccountLengthMonths || contact?.currentBankDuration),
    bank_account_number: contact?.bankAccountNumber || contact?.bankAccount || "",
    drivers_license_number: contact?.driversLicenseNumber || contact?.licenseID || "",
    drivers_license_state: contact?.driversLicenseState || contact?.issuingState || "",
    credit_score: mapCreditScore(contact?.creditScoreRating),
    ssn: (ssn || "").replace(/\D/g, ""),
    switch_vertical: "0",
    ref: "10",
    click_id: getClickId(sessionStorage.getItem("utm_campaign")) || "",
  };

  // Build query string
  const queryString = Object.entries(params)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");

  return queryString;
};
